import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Grid, 
  Card, 
  CardContent, 
  CardMedia, 
  Button, 
  Tabs,
  Tab,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemAvatar,
  Avatar,
  FormGroup,
  FormControlLabel,
  Checkbox,
  TextField,
  InputAdornment,
  Slider,
  IconButton,
  Snackbar,
  Alert,
  Chip,
  Rating,
  Stack,
  Badge,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Fade,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
  Switch
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { 
  Store as StoreIcon,
  LocationOn as LocationIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Category as CategoryIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  ShoppingCart as ShoppingCartIcon,
  AccessTime as TimeIcon,
  People as PeopleIcon,
  Star as StarIcon,
  CalendarToday as CalendarIcon,
  TrendingUp as TrendingIcon,
  LocalOffer as OfferIcon,
  BusinessCenter as BusinessIcon,
  Verified as VerifiedIcon,
  Info as InfoIcon,
  Map as MapIcon,
  Close as CloseIcon,
  ArrowForward as ArrowForwardIcon,
  CheckCircle as CheckCircleIcon,
  LocalPharmacy as LocalPharmacyIcon,
  Storefront as StorefrontIcon,
  Grass as GrassIcon,
  Restaurant as RestaurantIcon,
  Computer as ElectronicsIcon,
  Checkroom as GarmentsIcon,
  Science as ChemicalIcon,
  Build as HardwareIcon,
  FaceRetouchingNatural as CosmeticsIcon,
  LocalFlorist as LocalFloristIcon,
  MenuBook as BookIcon,
  Handyman as HandymanIcon,
  Public as PublicIcon
} from '@mui/icons-material';
import { Link, useParams, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useCart } from '../contexts/CartContext';
import { useLocation } from '../contexts/LocationContext';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import bazarDetailBn from '../i18n/bn/bazarDetail';
import bazarDetailEn from '../i18n/en/bazarDetail';
import bazarDetailNp from '../i18n/np/bazarDetail';
import bazarDetailAf from '../i18n/af/bazarDetail';
import bazarDetailCn from '../i18n/cn/bazarDetail';

// Fix Leaflet marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Styled components
const HeroSection = styled(Box)(({ theme }) => ({
  position: 'relative',
  color: 'white',
  padding: theme.spacing(8, 0),
  overflow: 'hidden',
  minHeight: '500px',
  display: 'flex',
  alignItems: 'center',
  
  // Video background
  '& .hero-video': {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    zIndex: -2,
  },
  
  // Overlay
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.8) 0%, rgba(118, 75, 162, 0.8) 100%)',
    zIndex: -1,
  }
}));

const StatsCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  textAlign: 'center',
  background: 'rgba(255,255,255,0.95)',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(255,255,255,0.2)',
  borderRadius: theme.spacing(2),
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
  }
}));

// Interactive Star Rating Component
const InteractiveStarRating = ({ rating, onRatingChange }) => {
  const [hoverRating, setHoverRating] = useState(0);
  const [selectedRating, setSelectedRating] = useState(rating);

  const handleMouseMove = (event, starIndex) => {
    event.stopPropagation(); // Prevent container's mouse move handler
    const rect = event.currentTarget.getBoundingClientRect();
    const width = rect.width;
    const x = event.clientX - rect.left;
    const percentage = x / width;
    
    // Calculate fractional rating (0.1 precision)
    const clampedPercentage = Math.max(0.1, Math.min(1, percentage));
    const finalRating = starIndex + clampedPercentage;
    
    setHoverRating(Math.round(finalRating * 10) / 10);
  };

  const handleMouseLeave = () => {
    setHoverRating(0);
  };

  const handleClick = () => {
    if (hoverRating > 0) {
      setSelectedRating(hoverRating);
      onRatingChange(hoverRating);
    }
  };

  // Determine which rating to use for display
  const currentRating = hoverRating || selectedRating;

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      alignItems: 'center',
      gap: 2
    }}>
      <Box 
        sx={{ 
          display: 'flex', 
          gap: 0.5,
          cursor: 'pointer',
          userSelect: 'none',
          paddingRight: '20px', // Extra space on the right for easier 5.0 selection
          position: 'relative'
        }}
        onMouseLeave={handleMouseLeave}
        onMouseMove={(e) => {
          // Handle mouse movement in the extra padding area
          const container = e.currentTarget;
          const rect = container.getBoundingClientRect();
          const x = e.clientX - rect.left;
          const containerWidth = rect.width - 20; // Subtract padding
          
          // If mouse is in the padding area (beyond stars), set to 5.0
          if (x > containerWidth) {
            setHoverRating(5.0);
          }
        }}
      >
        {[1, 2, 3, 4, 5].map((starNumber) => {
          // Check if this star should be filled
          const isFullyFilled = currentRating >= starNumber;
          const isPartiallyFilled = currentRating > starNumber - 1 && currentRating < starNumber;
          const fillPercentage = isPartiallyFilled ? ((currentRating - (starNumber - 1)) * 100) : (isFullyFilled ? 100 : 0);
          
          return (
            <Box
              key={starNumber}
              sx={{
                position: 'relative',
                fontSize: '2.5rem',
                lineHeight: 1,
                transition: 'all 0.1s ease'
              }}
              onMouseMove={(e) => handleMouseMove(e, starNumber - 1)}
              onClick={handleClick}
            >
              {/* Background star (gray) */}
              <Box
                sx={{
                  color: '#e0e0e0'
                }}
              >
                ★
              </Box>
              
              {/* Foreground star (yellow) */}
              {fillPercentage > 0 && (
                <Box
                  sx={{
                    color: '#ffc107',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    overflow: 'hidden',
                    width: `${fillPercentage}%`,
                    transition: 'width 0.1s ease'
                  }}
                >
                  ★
                </Box>
              )}
            </Box>
          );
        })}
      </Box>
      
      {/* Rating Display */}
      <Box sx={{ 
        textAlign: 'center',
        minHeight: '60px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center'
      }}>
        <Typography 
          variant="h4" 
          sx={{ 
            fontWeight: 700,
            color: hoverRating ? 'primary.main' : 'text.primary',
            transition: 'color 0.2s ease'
          }}
        >
          {currentRating.toFixed(1)}
        </Typography>
        <Typography 
          variant="caption" 
          color="text.secondary"
          sx={{ 
            opacity: hoverRating ? 1 : 0.7,
            transition: 'opacity 0.2s ease'
          }}
        >
          {hoverRating ? 'ক্লিক করে নির্বাচন করুন' : 'তারকা চিহ্নের উপর হোভার করুন'}
        </Typography>
      </Box>
    </Box>
  );
};

// Review Dialog Component
const ReviewDialog = ({ open, onClose, onSubmit, bazarName }) => {
  const [reviewData, setReviewData] = useState({
    name: '',
    email: '',
    rating: 5,
    comment: '',
    isGuest: false
  });

  const handleInputChange = (field) => (event) => {
    setReviewData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };



  const handleGuestToggle = (event) => {
    setReviewData(prev => ({
      ...prev,
      isGuest: event.target.checked
    }));
  };

  const handleSubmit = () => {
    if (!reviewData.comment.trim()) {
      alert('দয়া করে আপনার মতামত লিখুন।');
      return;
    }
    
    // Only validate name when NOT in guest mode (when fields are visible)
    if (!reviewData.isGuest && !reviewData.name.trim()) {
      alert('দয়া করে আপনার নাম লিখুন।');
      return;
    }

    onSubmit(reviewData);
    
    // Reset form
    setReviewData({
      name: '',
      email: '',
      rating: 5,
      comment: '',
      isGuest: false
    });
  };

  const handleClose = () => {
    onClose();
    // Reset form on close
    setReviewData({
      name: '',
      email: '',
      rating: 5,
      comment: '',
      isGuest: false
    });
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableScrollLock={true}
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 20px 40px rgba(0,0,0,0.1)'
        }
      }}
    >
      <DialogTitle sx={{ 
        textAlign: 'center',
        background: 'linear-gradient(135deg, #4a2496 0%, #0373d6 100%)',
        color: 'white',
        borderRadius: '12px 12px 0 0',
        position: 'relative'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
          <StarIcon sx={{ fontSize: 32, mr: 1 }} />
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            রিভিউ লিখুন
          </Typography>
        </Box>
        <Typography variant="body2" sx={{ opacity: 0.9 }}>
          {bazarName} সম্পর্কে আপনার মতামত দিন
        </Typography>
        <IconButton
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: 'white',
            '&:hover': {
              backgroundColor: 'rgba(255,255,255,0.1)'
            }
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 4 }}>
        {/* Guest/User Toggle */}
        <Box sx={{ mb: 3 }}>
          <FormControlLabel
            control={
              <Switch
                checked={reviewData.isGuest}
                onChange={handleGuestToggle}
                color="primary"
              />
            }
            label="গেস্ট হিসেবে রিভিউ করুন"
          />
        </Box>

        {/* Name Field - Hidden when guest toggle is ON */}
        {!reviewData.isGuest && (
          <TextField
            fullWidth
            label="আপনার নাম"
            value={reviewData.name}
            onChange={handleInputChange('name')}
            required
            sx={{ mb: 3 }}
            placeholder="আপনার নাম লিখুন"
          />
        )}

        {/* Email Field - Hidden when guest toggle is ON */}
        {!reviewData.isGuest && (
          <TextField
            fullWidth
            label="ইমেইল (ঐচ্ছিক)"
            type="email"
            value={reviewData.email}
            onChange={handleInputChange('email')}
            sx={{ mb: 3 }}
            placeholder="আপনার ইমেইল ঠিকানা"
          />
        )}

        {/* Interactive Star Rating */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
            রেটিং দিন
          </Typography>
          <InteractiveStarRating
            rating={reviewData.rating}
            onRatingChange={(newRating) => setReviewData(prev => ({ ...prev, rating: newRating }))}
          />
        </Box>

        {/* Comment Field */}
        <TextField
          fullWidth
          label="আপনার মতামত"
          multiline
          rows={4}
          value={reviewData.comment}
          onChange={handleInputChange('comment')}
          required
          placeholder="এই বাজার সম্পর্কে আপনার অভিজ্ঞতা শেয়ার করুন..."
          sx={{ mb: 3 }}
        />

        <Typography variant="body2" color="text.secondary" sx={{ 
          textAlign: 'center',
          fontStyle: 'italic'
        }}>
          আপনার রিভিউ অন্যদের সাহায্য করবে সঠিক সিদ্ধান্ত নিতে
        </Typography>
      </DialogContent>

      <DialogActions sx={{ 
        p: 3, 
        pt: 1,
        justifyContent: 'space-between',
        borderTop: '1px solid',
        borderColor: 'divider'
      }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600
          }}
        >
          বাতিল
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            background: 'linear-gradient(45deg, #4a2496 30%, #0373d6 90%)',
            '&:hover': {
              background: 'linear-gradient(45deg, #3d1f7a 30%, #0256a8 90%)',
            }
          }}
          startIcon={<StarIcon />}
        >
          রিভিউ জমা দিন
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const BazarDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { updateLocation } = useLocation();
  const [tabValue, setTabValue] = useState(0);
  const [currentLanguage, setCurrentLanguage] = useState((typeof window !== 'undefined' && localStorage.getItem('preferredLanguage')) || 'bn');
  const t = currentLanguage === 'en' ? bazarDetailEn : 
            currentLanguage === 'np' ? bazarDetailNp : 
            currentLanguage === 'af' ? bazarDetailAf : 
            currentLanguage === 'cn' ? bazarDetailCn : 
            bazarDetailBn;
  const [bazar, setBazar] = useState(null);
  const [stores, setStores] = useState([]);
  const [products, setProducts] = useState([]);
  const [featuredStores, setFeaturedStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [priceRange, setPriceRange] = useState([0, 5000]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [cartSnackbar, setCartSnackbar] = useState({
    open: false,
    message: '',
  });
  const [locationPopupOpen, setLocationPopupOpen] = useState(false);
  const [reviewPopupOpen, setReviewPopupOpen] = useState(false);

  // Bazar menu items from header
  const bazarMenuItems = [
    { label: 'নিত্যপণ্যের', path: '/daily-bazar', icon: StorefrontIcon },
    { label: 'ঔষধ', path: '/medicine-bazar', icon: LocalPharmacyIcon },
    { label: 'ফ্রেশ খাবার', path: '/food-bazar', icon: RestaurantIcon },
    { label: 'শস্য', path: '/grains', icon: GrassIcon },
    { label: 'জাতীয় পণ্য', path: '/national-bazar', icon: PublicIcon },
    { label: 'গার্মেন্টস', path: '/garments-bazar', icon: GarmentsIcon },
    { label: 'কসমেটিক্স', path: '/cosmetics-bazar', icon: CosmeticsIcon },
    { label: 'বই', path: '/book-bazar', icon: BookIcon },
    { label: 'নার্সারি', path: '/nursery-bazar', icon: LocalFloristIcon },
    { label: 'ইলেক্ট্রনিক্স', path: '/electronics-bazar', icon: ElectronicsIcon },
    { label: 'কেমিক্যাল', path: '/chemical-bazar', icon: ChemicalIcon },
    { label: 'হার্ডওয়্যার', path: '/hardware-bazar', icon: HardwareIcon },
    { label: 'সার্ভিস', path: '/service-bazar', icon: HandymanIcon }
  ];

  // Enhanced mock data matching BazarList data
  useEffect(() => {
    // In a real app, this would be an API call
    setTimeout(() => {
      // Match data from BazarList with slugs
      const bazarData = {
        'chanchkoir-bazar': {
          id: 1,
          slug: 'chanchkoir-bazar',
          name: 'চাচকৈড় বাজার',
          englishName: 'Chanchkoir Bazar',
          district: 'নোয়াখালী',
          upazilla: 'চাচকৈড়',
          size: 'বড়',
          storeCount: 145,
          rating: 4.5,
          established: '১৯৮৫',
          dailyVisitors: '৫০০০+',
          specialties: ['তাজা মাছ', 'কৃষি পণ্য', 'হস্তশিল্প'],
          description: 'ঐতিহ্যবাহী মাছের বাজার যা স্থানীয় কৃষি পণ্যের জন্য বিখ্যাত। এই বাজারে প্রতিদিন হাজারো মানুষের সমাগম হয় এবং এটি স্থানীয় অর্থনীতির প্রাণকেন্দ্র হিসেবে কাজ করে।',
          fullDescription: 'চাচকৈড় বাজার নোয়াখালী জেলার একটি প্রাচীন ও ঐতিহ্যবাহী বাজার। ১৯৮৫ সালে প্রতিষ্ঠিত এই বাজারটি মূলত তাজা মাছের জন্য বিখ্যাত হলেও বর্তমানে এখানে সব ধরনের দৈনন্দিন প্রয়োজনীয় পণ্য পাওয়া যায়। বাজারটিতে ১৪৫টি দোকান রয়েছে এবং প্রতিদিন ৫০০০+ মানুষ এখানে কেনাকাটা করতে আসেন।',
          categories: ['তাজা মাছ', 'কৃষি পণ্য', 'হস্তশিল্প', 'মুদি', 'শাকসবজি', 'ফলমূল'],
          address: 'চাচকৈড়, নোয়াখালী, বাংলাদেশ',
          phone: '+৮৮০ ১২৩৪ ৫৬৭৮৯০',
          email: 'info@chanchkoirbazar.com',
          openingHours: 'সকাল ৬:০০ - রাত ৯:০০',
          weeklyHoliday: 'শুক্রবার',
          parkingAvailable: true,
          atmAvailable: true,
          wheelchairAccessible: false,
          coordinates: [22.8696, 91.0968] // Noakhali coordinates
        },
        'khubjipur-bazar': {
          id: 2,
          slug: 'khubjipur-bazar',
          name: 'খুবজিপুর বাজার',
          englishName: 'Khubjipur Bazar',
          district: 'কুমিল্লা',
          upazilla: 'খুবজিপুর',
          size: 'মধ্যম',
          storeCount: 82,
          rating: 4.2,
          established: '১৯৯২',
          dailyVisitors: '৩০০০+',
          specialties: ['কাপড়', 'গহনা', 'মুদি'],
          description: 'স্থানীয় কাপড় ও গহনার জন্য প্রসিদ্ধ ঐতিহ্যবাহী বাজার।',
          fullDescription: 'খুবজিপুর বাজার কুমিল্লা জেলার একটি মধ্যম আকারের বাজার যা বিশেষত কাপড় ও গহনার জন্য বিখ্যাত। ১৯৯২ সালে প্রতিষ্ঠিত এই বাজারে ৮২টি দোকান রয়েছে।',
          categories: ['কাপড়', 'গহনা', 'মুদি', 'জুতা', 'সৌন্দর্য পণ্য'],
          address: 'খুবজিপুর, কুমিল্লা, বাংলাদেশ',
          phone: '+৮৮০ ১২৩৪ ৫৬৭৮৯১',
          email: 'info@khubjipurbazar.com',
          openingHours: 'সকাল ৭:০০ - রাত ৮:০০',
          weeklyHoliday: 'সোমবার',
          parkingAvailable: true,
          atmAvailable: false,
          wheelchairAccessible: true,
          coordinates: [23.4607, 91.1809] // Cumilla coordinates
        }
      };

      const currentBazar = bazarData[slug] || bazarData['chanchkoir-bazar'];
      
      setBazar({
        ...currentBazar,
        image: `/api/placeholder/1200/400?text=${currentBazar.englishName}`,
      });

      setStores([
        { id: 1, name: 'তাজা শাকসবজি দোকান', image: `https://picsum.photos/300/200?random=1`, category: 'শাকসবজি' },
        { id: 2, name: 'মাংসের স্বর্গ', image: `https://picsum.photos/300/200?random=2`, category: 'মাংস ও মাছ' },
        { id: 3, name: 'ফলের হেভেন', image: `https://picsum.photos/300/200?random=3`, category: 'ফলমূল' },
        { id: 4, name: 'মসলা ওয়ার্ল্ড', image: `https://picsum.photos/300/200?random=4`, category: 'মুদি' },
        { id: 5, name: 'ফ্যাশন হাব', image: `https://picsum.photos/300/200?random=5`, category: 'কাপড়-চোপড়' },
        { id: 6, name: 'টেক কর্নার', image: `https://picsum.photos/300/200?random=6`, category: 'ইলেকট্রনিক্স' },
      ]);
      
      // Generate 10 featured stores for the sidebar
      setFeaturedStores([
        { id: 1, name: 'তাজা শাকসবজি দোকান', logo: `https://picsum.photos/50/50?random=1`, rating: 4.8 },
        { id: 2, name: 'মাংসের স্বর্গ', logo: `https://picsum.photos/50/50?random=2`, rating: 4.6 },
        { id: 3, name: 'ফলের হেভেন', logo: `https://picsum.photos/50/50?random=3`, rating: 4.9 },
        { id: 4, name: 'মসলা ওয়ার্ল্ড', logo: `https://picsum.photos/50/50?random=4`, rating: 4.3 },
        { id: 5, name: 'ফ্যাশন হাব', logo: `https://picsum.photos/50/50?random=5`, rating: 4.5 },
        { id: 6, name: 'টেক কর্নার', logo: `https://picsum.photos/50/50?random=6`, rating: 4.7 },
        { id: 7, name: 'মিষ্টির দোকান', logo: `https://picsum.photos/50/50?random=7`, rating: 4.4 },
        { id: 8, name: 'হস্তশিল্প কেন্দ্র', logo: `https://picsum.photos/50/50?random=8`, rating: 4.2 },
        { id: 9, name: 'দুগ্ধ পণ্য', logo: `https://picsum.photos/50/50?random=9`, rating: 4.0 },
        { id: 10, name: 'জৈব খামার', logo: `https://picsum.photos/50/50?random=10`, rating: 4.5 },
      ]);

      // Generate mock products
      setProducts([
        { id: 1, name: 'তাজা টমেটো', price: 120, category: 'শাকসবজি', image: 'https://picsum.photos/200/200?random=1', store: 'তাজা শাকসবজি দোকান' },
        { id: 2, name: 'জৈব আপেল', price: 250, category: 'ফলমূল', image: 'https://picsum.photos/200/200?random=2', store: 'ফলের হেভেন' },
        { id: 3, name: 'প্রিমিয়াম গরুর মাংস', price: 850, category: 'মাংস ও মাছ', image: 'https://picsum.photos/200/200?random=3', store: 'মাংসের স্বর্গ' },
        { id: 4, name: 'বাসমতি চাল', price: 180, category: 'মুদি', image: 'https://picsum.photos/200/200?random=4', store: 'মসলা ওয়ার্ল্ড' },
        { id: 5, name: 'সুতি টি-শার্ট', price: 450, category: 'কাপড়-চোপড়', image: 'https://picsum.photos/200/200?random=5', store: 'ফ্যাশন হাব' },
        { id: 6, name: 'ওয়্যারলেস ইয়ারবাড', price: 1200, category: 'ইলেকট্রনিক্স', image: 'https://picsum.photos/200/200?random=6', store: 'টেক কর্নার' },
        { id: 7, name: 'তাজা আলু', price: 80, category: 'শাকসবজি', image: 'https://picsum.photos/200/200?random=7', store: 'তাজা শাকসবজি দোকান' },
        { id: 8, name: 'আঙ্গুর', price: 220, category: 'ফলমূল', image: 'https://picsum.photos/200/200?random=8', store: 'ফলের হেভেন' },
        { id: 9, name: 'মুরগি', price: 320, category: 'মাংস ও মাছ', image: 'https://picsum.photos/200/200?random=9', store: 'মাংসের স্বর্গ' },
        { id: 10, name: 'মসলা মিক্স', price: 150, category: 'মুদি', image: 'https://picsum.photos/200/200?random=10', store: 'মসলা ওয়ার্ল্ড' },
        { id: 11, name: 'জিন্স', price: 750, category: 'কাপড়-চোপড়', image: 'https://picsum.photos/200/200?random=11', store: 'ফ্যাশন হাব' },
        { id: 12, name: 'পাওয়ার ব্যাংক', price: 900, category: 'ইলেকট্রনিক্স', image: 'https://picsum.photos/200/200?random=12', store: 'টেক কর্নার' },
      ]);
      
      setLoading(false);
    }, 500);
  }, [slug]);

  // React to language change
  useEffect(() => {
    const updateLang = () => {
      const lang = (typeof window !== 'undefined' && localStorage.getItem('preferredLanguage')) || 'bn';
      setCurrentLanguage(lang);
    };
    const storageHandler = (e) => {
      if (e.key === 'preferredLanguage') updateLang();
    };
    window.addEventListener('preferredLanguageChanged', updateLang);
    window.addEventListener('storage', storageHandler);
    return () => {
      window.removeEventListener('preferredLanguageChanged', updateLang);
      window.removeEventListener('storage', storageHandler);
    };
  }, []);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleCategoryChange = (event) => {
    const { value, checked } = event.target;
    if (checked) {
      setSelectedCategories([...selectedCategories, value]);
    } else {
      setSelectedCategories(selectedCategories.filter(cat => cat !== value));
    }
  };

  const handlePriceRangeChange = (event, newValue) => {
    setPriceRange(newValue);
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  // Handle navigation to product detail page
  const handleProductClick = (productId) => {
    navigate(`/product/${productId}`);
  };

  // Handle add to cart
  const handleAddToCart = (event, product) => {
    event.stopPropagation(); // Prevent navigation to product detail
    
    // Add to cart using CartContext
    addToCart(product);
    
    // Show success message
    setCartSnackbar({
      open: true,
      message: `${product.name} কার্টে যোগ করা হয়েছে!`,
    });
    
    setTimeout(() => {
      setCartSnackbar({ open: false, message: '' });
    }, 3000);
  };

  // Close cart snackbar
  const handleCloseSnackbar = () => {
    setCartSnackbar({ ...cartSnackbar, open: false });
  };

  // Handle setting this bazar as current location
  const handleSetBazarLocation = () => {
    const locationData = {
      district: bazar.district,
      upazilla: bazar.upazilla,
      bazar: bazar.name
    };
    updateLocation(locationData);
    
    // Show the menu popup
    setLocationPopupOpen(true);
  };

  const handleLocationDialogClose = () => {
    setLocationPopupOpen(false);
  };

  const handleMenuItemClick = (path) => {
    setLocationPopupOpen(false);
    navigate(path);
  };

  const handleReviewPopupOpen = () => {
    setReviewPopupOpen(true);
  };

  const handleReviewPopupClose = () => {
    setReviewPopupOpen(false);
  };

  const handleReviewSubmit = (reviewData) => {
    // Here you would normally send the review to your backend
    console.log('Review submitted:', reviewData);
    
    // Show success message
    setCartSnackbar({
      open: true,
      message: 'আপনার রিভিউ সফলভাবে জমা দেওয়া হয়েছে!'
    });
    
    // Close the popup
    setReviewPopupOpen(false);
    
    // In a real app, you would refresh the reviews list here
  };

  // Filter products based on selected filters
  const filteredProducts = products.filter(product => {
    // Filter by search query
    if (searchQuery && !product.name.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }
    
    // Filter by category if any categories are selected
    if (selectedCategories.length > 0 && !selectedCategories.includes(product.category)) {
      return false;
    }
    
    // Filter by price range
    if (product.price < priceRange[0] || product.price > priceRange[1]) {
      return false;
    }
    
    return true;
  });

  if (loading) {
    return (
      <>
        <Header />
        <Container maxWidth="lg" sx={{ mt: 4, mb: 8, minHeight: '60vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <Typography>{t.loading}</Typography>
        </Container>
        <Footer />
      </>
    );
  }

  // Sidebar component with featured stores and filters
  const SidebarComponent = () => (
    <>
      {/* Product Filters - Now in position 1 */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">ফিল্টার</Typography>
          <IconButton size="small">
            <FilterListIcon />
          </IconButton>
        </Box>
        
        {/* Search Filter */}
        <TextField
          fullWidth
          placeholder="পণ্য খুঁজুন"
          size="small"
          value={searchQuery}
          onChange={handleSearchChange}
          sx={{ mb: 3 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        
        {/* Price Range Filter */}
        <Typography variant="subtitle2" gutterBottom>
          মূল্য সীমা
        </Typography>
        <Box sx={{ px: 1, mb: 3 }}>
          <Slider
            value={priceRange}
            onChange={handlePriceRangeChange}
            valueLabelDisplay="auto"
            min={0}
            max={5000}
          />
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2">৳{priceRange[0]}</Typography>
            <Typography variant="body2">৳{priceRange[1]}</Typography>
          </Box>
        </Box>
        
        {/* Category Filter */}
        <Typography variant="subtitle2" gutterBottom>
          ক্যাটাগরি
        </Typography>
        <FormGroup sx={{ mb: 2 }}>
          {bazar.categories.map((category) => (
            <FormControlLabel 
              key={category}
              control={
                <Checkbox 
                  size="small" 
                  value={category}
                  checked={selectedCategories.includes(category)}
                  onChange={handleCategoryChange}
                />
              } 
              label={<Typography variant="body2">{category}</Typography>}
            />
          ))}
        </FormGroup>
        
        {/* Reset Filter Button */}
        <Button 
          fullWidth 
          variant="outlined" 
          size="small"
          onClick={() => {
            setSelectedCategories([]);
            setPriceRange([0, 5000]);
            setSearchQuery('');
          }}
        >
          ফিল্টার রিসেট করুন
        </Button>
      </Paper>

      {/* Featured Stores - Now in position 2 */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>
          জনপ্রিয় দোকান
        </Typography>
        <List>
          {featuredStores.map((store) => (
            <ListItem 
              key={store.id} 
              button 
              component={Link} 
              to={`/stores/${store.slug || store.id}`}
              sx={{ 
                py: 1, 
                borderRadius: 1,
                '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' } 
              }}
            >
              <Box
                component="img"
                src={store.logo}
                alt={store.name}
                sx={{ 
                  width: 50, 
                  height: 50, 
                  mr: 2,
                  objectFit: 'cover',
                  borderRadius: '4px'
                }}
              />
              <ListItemText 
                primary={store.name} 
                secondary={
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                    <Box component="span" sx={{ color: 'warning.main', display: 'flex' }}>
                      {'★★★★★'}
                    </Box>
                    <Box component="span" sx={{ ml: 0.5, fontWeight: 'medium' }}>
                      ({store.rating ? store.rating.toFixed(1) : '0.0'})
                    </Box>
                  </Box>
                }
                primaryTypographyProps={{ 
                  variant: 'body2',
                  fontWeight: 'medium',
                  noWrap: true
                }}
                secondaryTypographyProps={{
                  variant: 'caption',
                }}
              />
            </ListItem>
          ))}
        </List>
      </Paper>
    </>
  );

  return (
    <>
      <Header />
      
      {/* Enhanced Hero Section */}
      <HeroSection>
        {/* Video Background */}
        <video
          className="hero-video"
          autoPlay
          muted
          loop
          playsInline
        >
          <source src="/videos/default.mp4" type="video/mp4" />
          {/* Fallback for browsers that don't support video */}
        </video>
        
        <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={8}>
              <Typography variant="h2" component="h1" gutterBottom sx={{ 
                fontWeight: 700,
                fontSize: { xs: '2rem', md: '3rem' },
                textShadow: '2px 2px 4px rgba(0,0,0,0.3)'
              }}>
                {bazar.name}
              </Typography>
              <Typography variant="h5" gutterBottom sx={{ 
                opacity: 0.9,
                fontStyle: 'italic',
                textShadow: '1px 1px 2px rgba(0,0,0,0.3)'
              }}>
                {bazar.englishName}
              </Typography>
              
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
                <Chip 
                  icon={<LocationIcon />} 
                  label={`${bazar.upazilla}, ${bazar.district}`}
                  sx={{ 
                    backgroundColor: 'rgba(255,255,255,0.2)', 
                    color: 'white',
                    backdropFilter: 'blur(10px)',
                    '& .MuiChip-icon': { color: 'white' }
                  }} 
                />
                <Chip 
                  icon={<StarIcon />} 
                  label={`${bazar.rating} রেটিং`}
                  sx={{ 
                    backgroundColor: 'rgba(255,193,7,0.8)', 
                    color: 'white',
                    backdropFilter: 'blur(10px)',
                    '& .MuiChip-icon': { color: 'white' }
                  }} 
                />
                <Chip 
                  icon={<CalendarIcon />} 
                  label={`প্রতিষ্ঠিত ${bazar.established}`}
                  sx={{ 
                    backgroundColor: 'rgba(76,175,80,0.8)', 
                    color: 'white',
                    backdropFilter: 'blur(10px)',
                    '& .MuiChip-icon': { color: 'white' }
                  }} 
                />
              </Box>
              
              <Typography variant="h6" sx={{ 
                opacity: 0.95,
                lineHeight: 1.6,
                maxWidth: '600px',
                textShadow: '1px 1px 2px rgba(0,0,0,0.3)'
              }}>
                {bazar.description}
              </Typography>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <StatsCard elevation={0}>
                    <BusinessIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                    <Typography variant="h4" color="primary" sx={{ fontWeight: 700 }}>
                      {bazar.storeCount}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t.hero.shops}
                    </Typography>
                  </StatsCard>
                </Grid>
                <Grid item xs={6}>
                  <StatsCard elevation={0}>
                    <PeopleIcon sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
                    <Typography variant="h4" color="success.main" sx={{ fontWeight: 700 }}>
                      {bazar.dailyVisitors}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t.hero.dailyVisitors}
                    </Typography>
                  </StatsCard>
                </Grid>
                <Grid item xs={6}>
                  <StatsCard elevation={0}>
                    <TrendingIcon sx={{ fontSize: 40, color: 'warning.main', mb: 1 }} />
                    <Typography variant="h4" color="warning.main" sx={{ fontWeight: 700 }}>
                      {bazar.size}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t.hero.size}
                    </Typography>
                  </StatsCard>
                </Grid>
                <Grid item xs={6}>
                  <StatsCard elevation={0}>
                    <VerifiedIcon sx={{ fontSize: 40, color: 'info.main', mb: 1 }} />
                    <Typography variant="h4" color="info.main" sx={{ fontWeight: 700 }}>
                      ★{bazar.rating}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t.hero.rating}
                    </Typography>
                  </StatsCard>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Container>
      </HeroSection>

      {/* Specialties Section */}
      <Container maxWidth="xl" sx={{ mt: -4, mb: 4, position: 'relative', zIndex: 2 }}>
        <Paper sx={{ p: 4, borderRadius: 3, boxShadow: '0 8px 32px rgba(0,0,0,0.1)' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 4 }}>
            {/* Left side - Specialties section */}
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <OfferIcon sx={{ mr: 1, color: 'primary.main' }} />
                {t.specialties.title}
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {bazar.specialties.map((specialty, index) => (
                  <Chip
                    key={index}
                    label={specialty}
                    variant="filled"
                    color="primary"
                    sx={{ 
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      '&:hover': {
                        backgroundColor: 'primary.dark',
                      }
                    }}
                  />
                ))}
              </Stack>
            </Box>

            {/* Right side - Button */}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Button
                variant="contained"
                onClick={handleSetBazarLocation}
                startIcon={<LocationIcon />}
                sx={{
                  borderRadius: '6px',
                  fontWeight: 600,
                  fontSize: '1rem',
                  textTransform: 'none',
                  background: 'linear-gradient(45deg, #4a2496 30%, #0373d6 90%)',
                  boxShadow: '0 4px 15px rgba(74, 36, 150, 0.3)',
                  minWidth: '220px',
                  height: '48px',
                  '&:hover': {
                    background: 'linear-gradient(45deg, #3d1f7a 30%, #0256a8 90%)',
                    boxShadow: '0 6px 20px rgba(74, 36, 150, 0.4)',
                    transform: 'translateY(-2px)',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                {t.specialties.buyHere}
              </Button>
            </Box>
          </Box>
        </Paper>
      </Container>

      <Container maxWidth="xl" sx={{ mb: 8 }}>
        <Paper sx={{ mb: 4, boxShadow: 'none', border: 'none' }}>
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange} 
            variant="fullWidth"
            textColor="primary"
            indicatorColor="primary"
          >
            <Tab label={t.tabs.overview} />
            <Tab label={t.tabs.stores} />
            <Tab label={t.tabs.reviews} />
          </Tabs>
          
          <Box sx={{ pt: 6, pb: 3 }} className="customClass">
            {tabValue === 0 && (
              <Box>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <InfoIcon sx={{ mr: 1, color: 'primary.main' }} />
                  {bazar.name}{t.overview.aboutSuffix}
                </Typography>
                
                <Grid container spacing={4}>
                  <Grid item xs={12} md={8}>
                    <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
                      <Typography variant="h6" gutterBottom>{t.overview.details}</Typography>
                      <Typography paragraph sx={{ lineHeight: 1.8, textAlign: 'justify' }}>
                        {bazar.fullDescription}
                      </Typography>
                      
                      <Divider sx={{ my: 3 }} />
                      
                      <Typography variant="h6" gutterBottom>{t.overview.contact}</Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <LocationIcon sx={{ mr: 2, color: 'primary.main' }} />
                            <Box>
                               <Typography variant="body2" color="text.secondary">{t.overview.address}</Typography>
                              <Typography variant="body1">{bazar.address}</Typography>
                            </Box>
                          </Box>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <PhoneIcon sx={{ mr: 2, color: 'success.main' }} />
                            <Box>
                               <Typography variant="body2" color="text.secondary">{t.overview.phone}</Typography>
                              <Typography variant="body1">{bazar.phone}</Typography>
                            </Box>
                          </Box>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <EmailIcon sx={{ mr: 2, color: 'info.main' }} />
                            <Box>
                               <Typography variant="body2" color="text.secondary">{t.overview.email}</Typography>
                              <Typography variant="body1">{bazar.email}</Typography>
                            </Box>
                          </Box>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <TimeIcon sx={{ mr: 2, color: 'warning.main' }} />
                            <Box>
                               <Typography variant="body2" color="text.secondary">{t.overview.openingHours}</Typography>
                              <Typography variant="body1">{bazar.openingHours}</Typography>
                            </Box>
                          </Box>
                        </Grid>
                      </Grid>
                    </Paper>

                    <Paper sx={{ p: 3, borderRadius: 2 }}>
                       <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                         <MapIcon sx={{ mr: 1, color: 'primary.main' }} />
                         {t.overview.location}
                       </Typography>
                      <Box sx={{ 
                        height: '300px', 
                        borderRadius: 2,
                        overflow: 'hidden',
                        border: '1px solid',
                        borderColor: 'grey.300',
                        '& .leaflet-container': {
                          height: '100%',
                          borderRadius: 2,
                        }
                      }}>
                        {bazar && bazar.coordinates ? (
                          <MapContainer
                            center={bazar.coordinates}
                            zoom={13}
                            style={{ height: '100%', width: '100%' }}
                            scrollWheelZoom={false}
                          >
                            <TileLayer
                              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                            />
                            <Marker position={bazar.coordinates}>
                              <Popup>
                                <Box sx={{ textAlign: 'center', p: 1 }}>
                                  <Typography variant="h6" gutterBottom>
                                    {bazar.name}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    {bazar.address}
                                  </Typography>
                                   <Typography variant="body2" sx={{ mt: 1 }}>
                                     {t.overview.rating}: {bazar.rating}/৫
                                   </Typography>
                                </Box>
                              </Popup>
                            </Marker>
                          </MapContainer>
                        ) : (
                          <Box sx={{ 
                            height: '100%', 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            bgcolor: 'grey.50'
                          }}>
                             <Typography color="text.secondary">{t.overview.mapLoading}</Typography>
                          </Box>
                        )}
                      </Box>
                    </Paper>
                  </Grid>
                  
                  <Grid item xs={12} md={4}>
                    <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
                       <Typography variant="h6" gutterBottom>{t.sidebarInfo.title}</Typography>
                      <Box sx={{ mb: 2 }}>
                         <Typography variant="body2" color="text.secondary">{t.sidebarInfo.established}</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>{bazar.established}</Typography>
                      </Box>
                      <Box sx={{ mb: 2 }}>
                         <Typography variant="body2" color="text.secondary">{t.sidebarInfo.weeklyHoliday}</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>{bazar.weeklyHoliday}</Typography>
                      </Box>
                      <Box sx={{ mb: 2 }}>
                         <Typography variant="body2" color="text.secondary">{t.sidebarInfo.size}</Typography>
                        <Chip label={bazar.size} color="primary" size="small" />
                      </Box>
                      <Box sx={{ mb: 2 }}>
                         <Typography variant="body2" color="text.secondary">{t.sidebarInfo.rating}</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Rating value={bazar.rating} readOnly precision={0.1} />
                          <Typography variant="body2">({bazar.rating})</Typography>
                        </Box>
                      </Box>
                    </Paper>

                    <Paper sx={{ p: 3, borderRadius: 2 }}>
                       <Typography variant="h6" gutterBottom>{t.facilities.title}</Typography>
                      <List dense>
                        <ListItem>
                          <ListItemIcon>
                             {bazar.parkingAvailable ? (
                               <Chip label={t.facilities.parkingYes} color="success" size="small" />
                             ) : (
                               <Chip label={t.facilities.parkingNo} color="error" size="small" />
                             )}
                          </ListItemIcon>
                        </ListItem>
                        <ListItem>
                          <ListItemIcon>
                             {bazar.atmAvailable ? (
                               <Chip label={t.facilities.atmYes} color="success" size="small" />
                             ) : (
                               <Chip label={t.facilities.atmNo} color="error" size="small" />
                             )}
                          </ListItemIcon>
                        </ListItem>
                        <ListItem>
                          <ListItemIcon>
                             {bazar.wheelchairAccessible ? (
                               <Chip label={t.facilities.wheelchairYes} color="success" size="small" />
                             ) : (
                               <Chip label={t.facilities.wheelchairNo} color="error" size="small" />
                             )}
                          </ListItemIcon>
                        </ListItem>
                      </List>
                    </Paper>
                  </Grid>
                </Grid>
              </Box>
            )}
            
            {tabValue === 1 && (
              <Box>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <StoreIcon sx={{ mr: 1, color: 'primary.main' }} />
                  {t.stores.title(bazar.name)}
                </Typography>
                <Grid container spacing={3}>
                  {stores.map((store) => (
                    <Grid item xs={12} sm={6} md={4} key={store.id}>
                      <Card 
                        sx={{ 
                          height: '100%', 
                          display: 'flex', 
                          flexDirection: 'column',
                          transition: 'transform 0.3s ease-in-out',
                          '&:hover': {
                            transform: 'translateY(-5px)',
                            boxShadow: 6,
                          },
                        }}
                      >
                        <CardMedia
                          component="img"
                          height="140"
                          image={store.image}
                          alt={store.name}
                        />
                        <CardContent sx={{ flexGrow: 1 }}>
                          <Typography gutterBottom variant="h6" component="h3">
                            {store.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {t.stores.categoryPrefix}{store.category}
                          </Typography>
                        </CardContent>
                        <Box sx={{ p: 2 }}>
                          <Button 
                            size="small" 
                            variant="contained" 
                            fullWidth
                            component={Link} 
                            to={`/stores/${store.slug || store.id}`}
                          >
                            {t.stores.viewStore}
                          </Button>
                        </Box>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}
            
            {tabValue === 2 && (
              <Box>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <StarIcon sx={{ mr: 1, color: 'primary.main' }} />
                  {t.reviews.title}
                </Typography>
                
                <Grid container spacing={4}>
                  <Grid item xs={12} md={8}>
                    {/* Reviews List */}
                    <Stack spacing={3}>
                      {[
                        {
                          id: 1,
                          name: 'রহিম উদ্দিন',
                          rating: 5,
                          date: '১৫ দিন আগে',
                          comment: 'চমৎকার বাজার! তাজা মাছ এবং ভাল দামে সব পণ্য পাওয়া যায়। দোকানদাররা খুবই বন্ধুত্বপূর্ণ।',
                          helpful: 12
                        },
                        {
                          id: 2,
                          name: 'ফাতেমা খাতুন',
                          rating: 4,
                          date: '১ মাস আগে',
                          comment: 'ভাল বাজার তবে পার্কিং এর ব্যবস্থা আরো উন্নত করা দরকার। পণ্যের মান ভাল।',
                          helpful: 8
                        },
                        {
                          id: 3,
                          name: 'করিম মিয়া',
                          rating: 5,
                          date: '২ মাস আগে',
                          comment: 'এই এলাকার সেরা বাজার। কৃষি পণ্যের জন্য খুবই বিখ্যাত। সবসময় তাজা পণ্য পাওয়া যায়।',
                          helpful: 15
                        },
                        {
                          id: 4,
                          name: 'সালমা বেগম',
                          rating: 4,
                          date: '৩ মাস আগে',
                          comment: 'দামাদামি করার সুযোগ আছে। বিভিন্ন ধরনের মসলা পাওয়া যায়। পরিবেশ পরিষ্কার।',
                          helpful: 6
                        }
                      ].map((review) => (
                        <Paper key={review.id} sx={{ p: 3, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                            <Box>
                              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                {review.name}
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <Rating value={review.rating} readOnly size="small" />
                                <Typography variant="body2" color="text.secondary">
                                  {review.date}
                                </Typography>
                              </Box>
                            </Box>
                            <Chip 
                              label={`${review.rating}/৫`} 
                              color={review.rating >= 4 ? 'success' : review.rating >= 3 ? 'warning' : 'error'}
                              size="small"
                            />
                          </Box>
                          
                          <Typography paragraph sx={{ lineHeight: 1.6, mb: 2 }}>
                            {review.comment}
                          </Typography>
                          
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Button size="small" variant="outlined" startIcon={<StarIcon />}>
                              সহায়ক ({review.helpful})
                            </Button>
                            <Button size="small" variant="text">
                              উত্তর দিন
                            </Button>
                          </Box>
                        </Paper>
                      ))}
                    </Stack>
                    
                    {/* Load More Button */}
                    <Box sx={{ textAlign: 'center', mt: 4 }}>
                      <Button variant="outlined" size="large">
                        {t.reviews.loadMore}
                      </Button>
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12} md={4}>
                    {/* Rating Summary */}
                    <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
                      <Typography variant="h6" gutterBottom>{t.reviews.summaryTitle}</Typography>
                      
                      <Box sx={{ textAlign: 'center', mb: 3 }}>
                        <Typography variant="h2" color="primary" sx={{ fontWeight: 700 }}>
                          {bazar.rating}
                        </Typography>
                        <Rating value={bazar.rating} readOnly precision={0.1} size="large" />
                        <Typography variant="body2" color="text.secondary">
                          {t.reviews.basedOn(85)}
                        </Typography>
                      </Box>
                      
                      <Stack spacing={1}>
                        {[5, 4, 3, 2, 1].map((star) => (
                          <Box key={star} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" sx={{ minWidth: '20px' }}>
                              {star}★
                            </Typography>
                            <Box sx={{ 
                              flex: 1, 
                              height: 8, 
                              bgcolor: 'grey.200', 
                              borderRadius: 1,
                              overflow: 'hidden'
                            }}>
                              <Box sx={{
                                height: '100%',
                                bgcolor: star >= 4 ? 'success.main' : star >= 3 ? 'warning.main' : 'error.main',
                                width: `${star === 5 ? 60 : star === 4 ? 25 : star === 3 ? 10 : star === 2 ? 3 : 2}%`,
                                transition: 'width 0.3s ease'
                              }} />
                            </Box>
                            <Typography variant="body2" color="text.secondary" sx={{ minWidth: '30px' }}>
                              {star === 5 ? '৫১' : star === 4 ? '২১' : star === 3 ? '৮' : star === 2 ? '৩' : '২'}
                            </Typography>
                          </Box>
                        ))}
                      </Stack>
                    </Paper>

                    {/* Write Review */}
                    <Paper sx={{ p: 3, borderRadius: 2 }}>
                      <Typography variant="h6" gutterBottom>{t.writeReview.title}</Typography>
                      <Typography variant="body2" color="text.secondary" paragraph>
                        {t.writeReview.subtitle}
                      </Typography>
                      <Button 
                        variant="contained" 
                        fullWidth 
                        startIcon={<StarIcon />}
                        onClick={handleReviewPopupOpen}
                        sx={{
                          background: 'linear-gradient(45deg, #4a2496 30%, #0373d6 90%)',
                          '&:hover': {
                            background: 'linear-gradient(45deg, #3d1f7a 30%, #0256a8 90%)',
                          }
                        }}
                      >
                        {t.writeReview.button}
                      </Button>
                    </Paper>
                  </Grid>
                </Grid>
              </Box>
            )}
          </Box>
        </Paper>
      </Container>
      <Footer />
      
      {/* Beautiful Location Change Popup */}
      <Dialog
        open={locationPopupOpen}
        onClose={handleLocationDialogClose}
        TransitionComponent={Fade}
        TransitionProps={{ timeout: 600 }}
        maxWidth="md"
        fullWidth
        disableScrollLock={true}
        PaperProps={{
          sx: {
            borderRadius: 4,
            background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
            boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
            overflow: 'visible'
          }
        }}
      >
        <DialogTitle sx={{ 
          textAlign: 'center', 
          pb: 1,
          background: 'linear-gradient(135deg, #4CAF50 0%, #45a049 100%)',
          color: 'white',
          borderRadius: '16px 16px 0 0',
          position: 'relative'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
            <CheckCircleIcon sx={{ fontSize: 40, mr: 2 }} />
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              অভিনন্দন!
            </Typography>
          </Box>
          <Typography variant="body1" sx={{ opacity: 0.9 }}>
            আপনার  বাজার সিলেক্ট হয়েছে
          </Typography>
          <IconButton
            onClick={handleLocationDialogClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: 'white',
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.1)'
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ p: 4, pt: 3 }}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <LocationIcon sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'text.primary' }}>
              {bazar.name}
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              এখন আপনার বর্তমান বাজার হিসেবে নির্বাচিত হয়েছে।
              আপনি যে ধরনের পণ্য কিনতে চান, নিচে থেকে সেই ক্যাটাগরি নির্বাচন করুন:
            </Typography>
          </Box>
          
          <Typography variant="h6" sx={{ mb: 3, textAlign: 'center', color: 'primary.main', fontWeight: 600 }}>
            আপনি কি কেনাকাটা করতে চান?
          </Typography>
          
          <Grid container spacing={2}>
            {bazarMenuItems.map((item, index) => {
              const IconComponent = item.icon;
              return (
                <Grid item xs={6} sm={4} md={3} key={index}>
                  <Paper
                    elevation={0}
                    onClick={() => handleMenuItemClick(item.path)}
                    sx={{
                      p: 2,
                      textAlign: 'center',
                      cursor: 'pointer',
                      border: '2px solid',
                      borderColor: 'grey.200',
                      borderRadius: 3,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        borderColor: 'primary.main',
                        backgroundColor: 'primary.50',
                        transform: 'translateY(-4px)',
                        boxShadow: '0 8px 25px rgba(0,0,0,0.1)'
                      }
                    }}
                  >
                    <IconComponent sx={{ 
                      fontSize: 40, 
                      color: 'primary.main', 
                      mb: 1,
                      transition: 'color 0.3s ease'
                    }} />
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontWeight: 600,
                        color: 'text.primary',
                        fontSize: '0.875rem'
                      }}
                    >
                      {item.label}
                    </Typography>
                    <ArrowForwardIcon sx={{ 
                      fontSize: 16, 
                      color: 'text.secondary',
                      mt: 0.5,
                      opacity: 0.7
                    }} />
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        </DialogContent>
        
        <DialogActions sx={{ 
          p: 3, 
          pt: 1,
          justifyContent: 'space-between',
          borderTop: '1px solid',
          borderColor: 'divider'
        }}>
          <Typography variant="body2" color="text.secondary">
            এই পণ্যগুলি {bazar.name} থেকে সংগ্রহ করা হবে
          </Typography>
          <Button
            onClick={handleLocationDialogClose}
            variant="outlined"
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600
            }}
          >
            পরে দেখব
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Review Writing Popup */}
      <ReviewDialog 
        open={reviewPopupOpen}
        onClose={handleReviewPopupClose}
        onSubmit={handleReviewSubmit}
        bazarName={bazar?.name}
      />
      
      {/* Snackbar for cart notifications */}
      <Snackbar
        open={cartSnackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
          {cartSnackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default BazarDetail;
